import { Button } from '@/shared/ui';
import { Loader2 } from 'lucide-react';
import { usePaymentReceiptPdf } from '../hooks/use-payment-receipt-pdf.hook';
import { usePaymentReceipt } from '../hooks/use-payment-receipt.hook';
import { PaymentReceiptDownload } from './payment-receipt-download.component';
import { PaymentReceiptNext } from './payment-receipt-next.component';
import { PaymentReceiptShell } from './payment-receipt-shell.component';
import { PaymentReceiptStatus } from './payment-receipt-status.component';
import { PaymentReceiptSummary } from './payment-receipt-summary.component';

type PaymentReceiptPageProps = {
  /** Token do link de retorno da Autra. */
  token: string;
};

/**
 * Página pública para onde a Autra devolve o segurado depois de pagar: diz se o
 * pagamento foi confirmado e resume o seguro dele. Não exige login.
 */
export function PaymentReceiptPage({ token }: PaymentReceiptPageProps) {
  const { view, brokerName, isPaid, isLoading, isRefreshing, canRetry, retry } =
    usePaymentReceipt(token);
  const pdf = usePaymentReceiptPdf(token);

  return (
    <PaymentReceiptShell>
      {isLoading || !view ? (
        <div
          role='status'
          className='flex flex-col items-center gap-3 py-10 text-sm text-alba-gray-500'
        >
          <Loader2
            aria-hidden='true'
            className='h-8 w-8 animate-spin text-alba-blue-600'
          />
          Carregando o seu comprovante…
        </div>
      ) : (
        <>
          <PaymentReceiptStatus
            tone={view.tone}
            title={view.title}
            message={view.message}
            refreshing={isRefreshing}
          />

          <PaymentReceiptSummary rows={view.rows} />

          {isPaid && (
            <PaymentReceiptDownload
              onDownload={() => pdf.mutate()}
              downloading={pdf.isPending}
              error={pdf.error?.message}
            />
          )}

          {view.next && (
            <PaymentReceiptNext
              title={view.next.title}
              description={view.next.description}
            />
          )}

          {canRetry && (
            <div className='flex justify-center'>
              <Button type='button' variant='primary' size='md' onClick={retry}>
                Tentar de novo
              </Button>
            </div>
          )}

          <p className='text-center text-xs leading-relaxed text-alba-gray-500'>
            {brokerName ? (
              <>
                Dúvidas sobre o seu seguro? Fale com a sua corretora:
                {/* Linha própria: o nome não quebra no meio da frase. */}
                <span className='block font-semibold text-alba-gray-600'>
                  {brokerName}
                </span>
              </>
            ) : (
              'Dúvidas sobre o seu seguro? Fale com a sua corretora.'
            )}
          </p>
        </>
      )}
    </PaymentReceiptShell>
  );
}
